/*
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { IAdaptor } from '../utils/IAdaptor';
import { ISecuritySetup } from '../../../domain/models/ISecuritySetup';
import SecuritySetupDto from '../../dto/SecuritySetupDto';

export default class SecuritySetupAdaptor implements IAdaptor<ISecuritySetup, SecuritySetupDto> {
  /**
     * This property is used to map properties from FE to properties in BE, such that when requesting some fields,
     * or filtering by some fields, the infrastructure layer knows what to request from the BE.
     * */
  domainToDtoPropertiesMap: { [K in keyof ISecuritySetup]?: keyof SecuritySetupDto } = {
    id: 'id',
    factors: 'factors',
    enrolledFactors: 'enrolledFactors',
    type: 'type',
    status: 'status',
    level: 'level',
    label: 'label',
    userId: 'userId',
    options: 'options',
    code: 'code',
    loaProof: 'loaProof'
  };

  /**
     * This is used mostly when we do get calls, so that data from BE is properly mapped to data in FE
     */
  fromDto(dto: SecuritySetupDto): Partial<ISecuritySetup> {
    return {
      id: dto.id,
      factors: dto.factors,
      enrolledFactors: dto.enrolledFactors,
      type: dto.type,
      status: dto.status,
      level: dto.level,
      label: dto.label,
      userId: dto.userId,
      options: dto.options,
      code: dto.code,
      loaProof: dto.loaProof
    };
  }

  /**
     * This function is used to map FE Domain models to BE models.
     * It will be called when we send a domain model to the infrastructure, and it needs to send a Dto to BE (post, patch, put).
     * Bear in mind that you can just write the properties that you use,
     * no need to add the properties that you don't need to send over the network.
     */
  toDto(model: Partial<ISecuritySetup>): Partial<SecuritySetupDto> {
    return {
      id: model.id,
      factors: model.factors,
      enrolledFactors: model.enrolledFactors,
      type: model.type,
      status: model.status,
      level: model.level,
      userId: model.userId,
      label: model.label,
      phoneNumber: model.phoneNumber,
      options: model.options,
      code: model.code,
      loaProof: model.loaProof
    };
  }
}
